"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./Icon";

type WorkflowType = "voice" | "lipi" | "claim" | "sahyogi";

interface WorkflowDef {
  id: WorkflowType;
  title: string;
  shortDesc: string;
  icon: "micro" | "file" | "shield" | "chat";
  color: string;
  gradientFrom: string;
  gradientTo: string;
  glowColor: string;
  accentBg: string;
}

const getAiWorkflowsVisualizerTranslations = (locale: string) => {
  if (locale === "hi") {
    return {
      voiceTitle: "Voice-MD",
      voiceShort: "आवाज से डिजिटल रिपोर्ट",
      lipiTitle: "Lipi AI",
      lipiShort: "Rx डिजिटलीकरण",
      claimTitle: "ClaimIQ",
      claimShort: "दावा सत्यापन",
      sahyogiTitle: "सहयोगी AI",
      sahyogiShort: "अपॉइंटमेंट सहायक",
      rawInput: "कच्चा इनपुट फीड",
      doctorSpeaking: "चिकित्सक बोल रहे हैं",
      voiceInputFull: "रमेश, 45, लंबे समय से धूम्रपान करने वाले। सुबह से सीने में दर्द और सांस लेने में कठिनाई। बीपी 145/95, SpO2 94%। टैब एस्पिरिन 325mg तुरंत निर्धारित। आपातकालीन ईसीजी और ट्रोपोनिन का आदेश।",
      voiceComplaintFull: "सुबह से सीने में तेज दर्द और सांस लेने में तकलीफ।",
      voiceVitalsFull: "बीपी: 145/95 mmHg | SpO2: 94% कमरे की हवा पर।",
      voiceDiagnosisFull: "तीव्र रोधगलन (AMI) का संदेह।",
      voiceMedicineFull: "टैब एस्पिरिन 325mg तुरंत (त्वरित मौखिक)।",
      voiceInvestigationFull: "आपातकालीन ईसीजी, सीरम ट्रोपोनिन-टी टेस्ट।",
      voiceHabitsFull: "दीर्घकालिक धूम्रपान करने वाले (15 वर्षों से 1 पैक/दिन)।",
      patientLabel: "मरीज: रमेश कुमार",
      tpaLabel: "TPA #182",
      treatmentLabel: "इलाज: ICU प्रबंधन · स्टार हेल्थ",
      identityVerified: "पहचान सत्यापित",
      identityError: "पहचान त्रुटि: दस्तावेजों में मरीज का नाम मेल नहीं खा रहा है",
      matched: "सत्यापित",
      procedureValidated: "प्रक्रिया और नोट्स सत्यापित",
      procedureError: "पैकेज त्रुटि: अपरिचित प्राथमिक कोड और लापता नोट्स",
      notes: "नोट्स",
      sahyogiPatient1: "नमस्ते, डॉ. शर्मा के साथ मेरा अपॉइंटमेंट पुनर्निर्धारित करना है।",
      sahyogiBot1: "बिल्कुल, डॉ. शर्मा के पास कल सुबह 10:00 बजे और दोपहर 2:30 बजे खाली स्लॉट हैं।",
      sahyogiPatient2: "सुबह 10:00 बजे ठीक रहेगा, धन्यवाद!",
      sahyogiBot2: "पुनर्निर्धारित किया गया! आपकी लाइव चैट पर पुष्टि भेज दी गई है।",
      symptomTriage: "1. लक्षण वर्गीकरण (Triage)",
      triageDesc: "लक्षण पूछें और मुख्य शिकायत दर्ज करें",
      liveChat: "लाइव चैट",
      sahyogiProcessor: "Sahyogi प्रोसेसर",
      triageBadge: "वर्गीकरण",
      severityRedFlags: "2. गंभीरता और रेड फ्लैग",
      severityDesc: "वास्तविक समय में गंभीरता और रेड फ्लैग का मूल्यांकन करें",
      triageAnalysis: "वर्गीकरण विश्लेषण",
      analyzing: "विश्लेषण जारी",
      specialistMatching: "3. विशेषज्ञ मिलान",
      specialistDesc: "EMR से सबसे उपयुक्त डॉक्टर के साथ मरीज का मिलान करें",
      emrMatchmaker: "EMR मिलानकर्ता",
      matchedBadge: "मेल खाया",
      slotBookingSync: "4. स्लॉट बुकिंग सिंक",
      slotDesc: "उपलब्ध समय स्लॉट की पुष्टि करें और HIMS के साथ सिंक करें",
      himsLedgerSync: "HIMS लेजर सिंक",
      confirmedBadge: "पुष्टि हुई",
      waitingAadhaar: "Aadhaar सत्यापन की प्रतीक्षा में...",
      otpVerified: "OTP सत्यापित",
      biometricDevice: "बायोमेट्रिक स्कैनिंग डिवाइस",
      livenessScan: "लिवनेस स्कैन...",
      fingerprintMatch: "फार्मेसी फिंगरप्रिंट मैच",
      lipiScanning: "दस्तावेज़ स्कैनिंग...",
      lipiProcessing: "AI मैपिंग और डिजिटलीकरण...",
      lipiComplete: "EMR सिंक पूर्ण!",
      claimIqLiveCheck: "ClaimIQ लाइव पूर्व-उड़ान जाँच",
      rejectionRisk: "अस्वीकृति जोखिम",
      autoCodingAudit: "ऑटो-कोडिंग और टैरिफ ऑडिट",
      finalBillMatched: "अंतिम बिल मिलान: 100% सही",
      allChecksPassed: "सभी जाँचें उत्तीर्ण: प्रस्तुत करने के लिए तैयार",
      lipiOcrInput: "प्रिस्क्रिप्शन OCR इनपुट",
      lipiClinicalOutput: "नैदानिक संरचित आउटपुट",
      rxMatched: "पर्ची मिलान: 5 दवाएं पहचानी गईं",
      sahyogiLiveWorkflow: "Sahyogi AI बुकिंग वर्कफ़्लो",
      sahyogiChatHistory: "सहयोगी चैट इतिहास",
      sahyogiSlotConfirmed: "अपॉइंटमेंट स्लॉट की पुष्टि: कल सुबह 10:00 बजे",
      lipiChiefComplaint: "मुख्य शिकायत",
      lipiVitals: "सजीव संकेत (Vitals)",
      lipiDiagnosis: "निदान (Diagnosis)",
      lipiPrescribedMed: "निर्धारित दवाएं",
      lipiInvestigations: "जांच (Investigations)",
      lipiSocialHistory: "सामाजिक इतिहास",
      recLive: "● रिकॉर्डिंग लाइव",
      acc: "सटीकता",
      ocrScanning: "● OCR स्कैनिंग",
      confidence: "सटीकता",
      aiProcessing: "● AI प्रोसेसिंग",
      formularyMap: "दवा सूची मिलान...",
      scanComplete: "✓ स्कैन पूर्ण",
      ruleset: "नियम सूची",
      auditing: "● ऑडिट जारी...",
      triageChatbot: "वर्गीकरण चैटबॉट v3.0",
      confirmed: "● पुष्टि हुई",
      active: "● सक्रिय",
      generating: "बन रहा है...",
      digitalReport: "डिजिटल रिपोर्ट",
      claimIqAuditing: "ClaimIQ ऑडिट",
      structuredOutput: "संरचित आउटपुट",
      parsingIntent: "इरादा पार्स किया जा रहा है...",
      hi: {
        hiWhatSymptoms: "नमस्ते! आज आपको क्या लक्षण महसूस हो रहे हैं?",
        hiWhatSymptomsResp: "नमस्ते, मुझे बुखार और सूखी खांसी है।",
        hiAnyBreathing: "समझ गया। क्या सांस लेने में कोई तकलीफ या सीने में दर्द है?",
        hiAnyBreathingResp: "नहीं, लेकिन मेरा बुखार 101 F के आसपास है।",
        hiRecommendGp: "मैं जनरल फिजिशियन से परामर्श करने की सलाह देता हूँ। क्या मैं इसे शेड्यूल करूँ?",
        hiRecommendGpResp: "हाँ, कृपया आज के लिए ही शेड्यूल करें।",
        hiAvailableToday: "डॉ. अनन्या शर्मा आज उपलब्ध हैं: 4:30 PM या 5:15 PM। कौन सा समय ठीक रहेगा?",
        hi430Works: "4:30 PM ठीक रहेगा।",
        hiConfirmBooking: "बुकिंग की पुष्टि: बुकिंग ID: CHIK-8921. विवरण चैट पर भेज दिया गया है।"
      },
      en: {
        hiWhatSymptoms: "Hi! What symptoms are you experiencing today?",
        hiWhatSymptomsResp: "Hi, I have a fever and dry cough.",
        hiAnyBreathing: "Understood. Any breathing difficulty or chest pain?",
        hiAnyBreathingResp: "No, but my fever is around 101 F.",
        hiRecommendGp: "I recommend consulting a General Physician. Shall I schedule it?",
        hiRecommendGpResp: "Yes, please schedule for today.",
        hiAvailableToday: "Dr. Ananya Sharma available today: 4:30 PM or 5:15 PM. Which works?",
        hi430Works: "4:30 PM works.",
        hiConfirmBooking: "Confirm Booking: Booking ID: CHIK-8921. Sent details to live chat."
      }
    };
  }
  return {
    voiceTitle: "Voice-MD",
    voiceShort: "Speech to Digital Report",
    lipiTitle: "Lipi AI",
    lipiShort: "Rx Digitisation",
    claimTitle: "ClaimIQ",
    claimShort: "Claims Validation",
    sahyogiTitle: "Sahyogi AI",
    sahyogiShort: "Sahyogi AI",
    rawInput: "Raw Input Feed",
    doctorSpeaking: "Doctor Speaking",
    voiceInputFull: "Ramesh, 45, chronic smoker. Chest pain & dyspnea since morning. BP 145/95, SpO2 94%. Prescribing Tab Aspirin 325mg stat. Ordering emergency ECG & Troponin.",
    voiceComplaintFull: "Acute chest pain & shortness of breath since morning.",
    voiceVitalsFull: "BP: 145/95 mmHg | SpO2: 94% on room air.",
    voiceDiagnosisFull: "Suspected Acute Myocardial Infar infarction (AMI).",
    voiceMedicineFull: "Tab Aspirin 325mg Stat (immediate oral).",
    voiceInvestigationFull: "Emergency ECG, Serum Troponin-T test.",
    voiceHabitsFull: "Chronic smoker (1 pack/day for 15 years).",
    patientLabel: "Patient: Ramesh Kumar",
    tpaLabel: "TPA #182",
    treatmentLabel: "Treatment: ICU Management · Star Health",
    identityVerified: "Identity Verified",
    identityError: "Identity Fraud: Patient name mismatch across documents",
    matched: "Matched",
    procedureValidated: "Procedure & Notes Validated",
    procedureError: "Package Error: Unrecognized primary code & missing operative notes",
    notes: "Notes",
    sahyogiPatient1: "Hi, need to reschedule my appt with Dr. Sharma.",
    sahyogiBot1: "Sure, Dr. Sharma has open slots tomorrow at 10:00 AM and 2:30 PM.",
    sahyogiPatient2: "10:00 AM works, thank you!",
    sahyogiBot2: "Rescheduled! Sent confirmation to your live chat.",
    symptomTriage: "1. Symptom Triage",
    triageDesc: "Ask symptoms and capture chief complaint",
    liveChat: "LIVE CHAT",
    sahyogiProcessor: "SAHYOGI PROCESSOR",
    triageBadge: "Triage",
    severityRedFlags: "2. Severity & Red Flags",
    severityDesc: "Evaluate severity and red flags in real-time",
    triageAnalysis: "TRIAGE ANALYSIS",
    analyzing: "Analyzing",
    specialistMatching: "3. Specialist Matching",
    specialistDesc: "Match patient with best-fit doctor from EMR",
    emrMatchmaker: "EMR MATCHMAKER",
    matchedBadge: "Matched",
    slotBookingSync: "4. Slot Booking Sync",
    slotDesc: "Confirm available time slots and sync with HIMS",
    himsLedgerSync: "HIMS LEDGER SYNC",
    confirmedBadge: "Confirmed",
    waitingAadhaar: "Waiting for Aadhaar verification...",
    otpVerified: "OTP Verified",
    biometricDevice: "BIOMETRIC SCANNING DEVICE",
    livenessScan: "Liveness scan...",
    fingerprintMatch: "Fingerprint Match",
    lipiScanning: "Scanning Document...",
    lipiProcessing: "AI mapping & digitising...",
    lipiComplete: "EMR Sync Complete!",
    claimIqLiveCheck: "ClaimIQ Live Pre-flight Check",
    rejectionRisk: "Rejection Risk",
    autoCodingAudit: "Auto-Coding & Tariff Audit",
    finalBillMatched: "Final bill matched: 100% compliant",
    allChecksPassed: "All checks passed: Ready to submit",
    lipiOcrInput: "Prescription OCR Input",
    lipiClinicalOutput: "Clinical Structured Output",
    rxMatched: "Rx matched: 5 medicines identified",
    sahyogiLiveWorkflow: "Sahyogi AI Booking Workflow",
    sahyogiChatHistory: "Sahyogi Chat History",
    sahyogiSlotConfirmed: "Slot confirmed: Tomorrow 10:00 AM",
    lipiChiefComplaint: "Chief Complaint",
    lipiVitals: "Vitals",
    lipiDiagnosis: "Diagnosis",
    lipiPrescribedMed: "Prescribed Medicine",
    lipiInvestigations: "Investigations",
    lipiSocialHistory: "Social History",
    recLive: "● REC LIVE",
    acc: "Acc.",
    ocrScanning: "● OCR SCANNING",
    confidence: "Confidence",
    aiProcessing: "● AI PROCESSING",
    formularyMap: "Formulary Map...",
    scanComplete: "✓ SCAN COMPLETE",
    ruleset: "Ruleset",
    auditing: "● Auditing...",
    triageChatbot: "Triage Chatbot v3.0",
    confirmed: "● Confirmed",
    active: "● Active",
    generating: "Generating...",
    digitalReport: "Digital Report",
    claimIqAuditing: "ClaimIQ Auditing",
    structuredOutput: "Structured output",
    parsingIntent: "Parsing intent...",
    hi: {
      hiWhatSymptoms: "Hi! What symptoms are you experiencing today?",
      hiWhatSymptomsResp: "Hi, I have a fever and dry cough.",
      hiAnyBreathing: "Understood. Any breathing difficulty or chest pain?",
      hiAnyBreathingResp: "No, but my fever is around 101 F.",
      hiRecommendGp: "I recommend consulting a General Physician. Shall I schedule it?",
      hiRecommendGpResp: "Yes, please schedule for today.",
      hiAvailableToday: "Dr. Ananya Sharma available today: 4:30 PM or 5:15 PM. Which works?",
      hi430Works: "4:30 PM works.",
      hiConfirmBooking: "Confirm Booking: Booking ID: CHIK-8921. Sent details to live chat."
    },
    en: {
      hiWhatSymptoms: "Hi! What symptoms are you experiencing today?",
      hiWhatSymptomsResp: "Hi, I have a fever and dry cough.",
      hiAnyBreathing: "Understood. Any breathing difficulty or chest pain?",
      hiAnyBreathingResp: "No, but my fever is around 101 F.",
      hiRecommendGp: "I recommend consulting a General Physician. Shall I schedule it?",
      hiRecommendGpResp: "Yes, please schedule for today.",
      hiAvailableToday: "Dr. Ananya Sharma available today: 4:30 PM or 5:15 PM. Which works?",
      hi430Works: "4:30 PM works.",
      hiConfirmBooking: "Confirm Booking: Booking ID: CHIK-8921. Sent details to live chat."
    }
  };
};

const getWorkflowsDef = (locale: string): WorkflowDef[] => {
  const t = getAiWorkflowsVisualizerTranslations(locale);
  return [
    {
      id: "voice",
      title: t.voiceTitle,
      shortDesc: t.voiceShort,
      icon: "micro",
      color: "#0A9E9A",
      gradientFrom: "#0A9E9A",
      gradientTo: "#06C5BF",
      glowColor: "rgba(10,158,154,0.20)",
      accentBg: "rgba(10,158,154,0.06)",
    },
    {
      id: "lipi",
      title: t.lipiTitle,
      shortDesc: t.lipiShort,
      icon: "file",
      color: "#2A9E5B",
      gradientFrom: "#2A9E5B",
      gradientTo: "#35C974",
      glowColor: "rgba(42,158,91,0.20)",
      accentBg: "rgba(42,158,91,0.06)",
    },
    {
      id: "claim",
      title: t.claimTitle,
      shortDesc: t.claimShort,
      icon: "shield",
      color: "#B88500",
      gradientFrom: "#B88500",
      gradientTo: "#E5A800",
      glowColor: "rgba(184,133,0,0.20)",
      accentBg: "rgba(184,133,0,0.06)",
    },
    {
      id: "sahyogi",
      title: t.sahyogiTitle,
      shortDesc: t.sahyogiShort,
      icon: "chat",
      color: "#CC2B58",
      gradientFrom: "#CC2B58",
      gradientTo: "#F0325F",
      glowColor: "rgba(204,43,88,0.20)",
      accentBg: "rgba(204,43,88,0.06)",
    },
  ];
};

export function AiWorkflowsVisualizer({ locale = "en" }: { locale?: string }) {
  const WORKFLOWS = getWorkflowsDef(locale);
  const t = getAiWorkflowsVisualizerTranslations(locale);
  
  const VOICE_INPUT_FULL = t.voiceInputFull;
  const VOICE_COMPLAINT_FULL = t.voiceComplaintFull;
  const VOICE_VITALS_FULL = t.voiceVitalsFull;
  const VOICE_DIAGNOSIS_FULL = t.voiceDiagnosisFull;
  const VOICE_MEDICINE_FULL = t.voiceMedicineFull;
  const VOICE_INVESTIGATION_FULL = t.voiceInvestigationFull;
  const VOICE_HABITS_FULL = t.voiceHabitsFull;
  
  const SAHYOGI_PATIENT_1 = t.sahyogiPatient1;
  const SAHYOGI_BOT_1 = t.sahyogiBot1;
  const SAHYOGI_PATIENT_2 = t.sahyogiPatient2;
  const SAHYOGI_BOT_2 = t.sahyogiBot2;
  const [active, setActive] = useState<WorkflowType>("voice");
  const [langCycle, setLangCycle] = useState(0);

  // --- Real-time Animation Loop Frame ---
  const [frame, setFrame] = useState(0);

  // --- Voice-MD Typing States ---
  const [voiceInput, setVoiceInput] = useState("");
  const [voiceComplaint, setVoiceComplaint] = useState("");
  const [voiceVitals, setVoiceVitals] = useState("");
  const [voiceDiagnosis, setVoiceDiagnosis] = useState("");
  const [voiceMedicine, setVoiceMedicine] = useState("");
  const [voiceInvestigation, setVoiceInvestigation] = useState("");
  const [voiceHabits, setVoiceHabits] = useState("");

  const isHoveredRef = useRef(false);
  const sahyogiChatRef = useRef<HTMLDivElement>(null);

  // Sync animation loop for real-time simulator steps
  useEffect(() => {
    setFrame(0);
    setVoiceInput("");
    setVoiceComplaint("");
    setVoiceVitals("");
    setVoiceDiagnosis("");
    setVoiceMedicine("");
    setVoiceInvestigation("");
    setVoiceHabits("");

    // Helper to get limit for each workflow
    const getFrameLimit = (id: WorkflowType): number => {
      switch (id) {
        case "voice": return 240;
        case "lipi": return 210;
        case "claim": return 210;
        case "sahyogi": return 310;
        default: return 280;
      }
    };

    const limit = getFrameLimit(active);

    const interval = setInterval(() => {
      setFrame((f) => {
        const nextFrame = f + 1;
        // Reset loop at limit frames
        if (nextFrame > limit) {
          setVoiceInput("");
          setVoiceComplaint("");
          setVoiceVitals("");
          setVoiceDiagnosis("");
          setVoiceMedicine("");
          setVoiceInvestigation("");
          setVoiceHabits("");

          if (isHoveredRef.current) {
            // Hovered: Loop the same section without transitioning
            return 0;
          } else {
            // Not hovered: Transition to the next section
            const currentIndex = WORKFLOWS.findIndex((w) => w.id === active);
            const nextIndex = (currentIndex + 1) % WORKFLOWS.length;
            setActive(WORKFLOWS[nextIndex].id);
            return 0;
          }
        }
        return nextFrame;
      });
    }, 45); // ~22 updates per second

    return () => clearInterval(interval);
  }, [active]);

  // Handle Voice-MD text slicing based on frame
  useEffect(() => {
    if (active !== "voice") return;

    setVoiceInput(VOICE_INPUT_FULL.slice(0, frame * 1.5));

    setVoiceComplaint(frame >= 15 ? VOICE_COMPLAINT_FULL.slice(0, (frame - 15) * 1.2) : "");
    setVoiceVitals(frame >= 50 ? VOICE_VITALS_FULL.slice(0, (frame - 50) * 1.3) : "");
    setVoiceDiagnosis(frame >= 90 ? VOICE_DIAGNOSIS_FULL.slice(0, (frame - 90) * 1.4) : "");
    setVoiceMedicine(frame >= 130 ? VOICE_MEDICINE_FULL.slice(0, (frame - 130) * 1.4) : "");
    setVoiceInvestigation(frame >= 160 ? VOICE_INVESTIGATION_FULL.slice(0, (frame - 160) * 1.4) : "");
    setVoiceHabits(frame >= 190 ? VOICE_HABITS_FULL.slice(0, (frame - 190) * 1.4) : "");
  }, [frame, active]);

  // Auto-scroll Sahyogi chat container to bottom when messages update
  useEffect(() => {
    if (active === "sahyogi" && sahyogiChatRef.current) {
      sahyogiChatRef.current.scrollTop = sahyogiChatRef.current.scrollHeight;
    }
  }, [frame, active]);

  const wf = WORKFLOWS.find((w) => w.id === active)!;

  // Lipi Animation Phases: scanning (0-90), processing (90-150), complete (150-210)
  const lipiPhase = (() => {
    if (active !== "lipi") return "idle";
    if (frame < 90) return "scanning";
    if (frame < 150) return "processing";
    return "complete";
  })();

  // Laser top position (0 to 100 percent)
  const lipiLaserPercent = (() => {
    if (active !== "lipi") return 0;
    if (frame < 90) return (frame / 90) * 100;
    return 100;
  })();

  // Calculate Lipi laser line top position based on frame
  const getLipiLaserTop = () => {
    return `${lipiLaserPercent}%`;
  };

  // Calculate ClaimIQ dynamic rejection risk score (%)
  const getClaimRejectionRisk = () => {
    if (active !== "claim") return "24.5%";
    if (frame < 30) return "24.5%";
    if (frame < 75) {
      const p = (frame - 30) / 45;
      return (24.5 - p * 12.3).toFixed(1) + "%";
    }
    if (frame < 125) {
      const p = (frame - 75) / 50;
      return (12.2 - p * 10.4).toFixed(1) + "%";
    }
    const p = Math.min(1, (frame - 125) / 40);
    return (1.8 - p * 1.0).toFixed(1) + "%";
  };

  // Helper to resolve Sahyogi dynamic step details based on the current frame
  const getSahyogiStepInfo = (f: number) => {
    if (f < 70) {
      return {
        index: 0,
        title: t.symptomTriage,
        desc: t.triageDesc,
        leftHeader: t.liveChat,
        rightHeader: t.sahyogiProcessor,
        badge: t.triageBadge,
      };
    } else if (f < 140) {
      return {
        index: 1,
        title: t.severityRedFlags,
        desc: t.severityDesc,
        leftHeader: t.liveChat,
        rightHeader: t.triageAnalysis,
        badge: t.analyzing,
      };
    } else if (f < 210) {
      return {
        index: 2,
        title: t.specialistMatching,
        desc: t.specialistDesc,
        leftHeader: t.liveChat,
        rightHeader: t.emrMatchmaker,
        badge: t.matchedBadge,
      };
    } else {
      return {
        index: 3,
        title: t.slotBookingSync,
        desc: t.slotDesc,
        leftHeader: t.liveChat,
        rightHeader: t.himsLedgerSync,
        badge: t.confirmedBadge,
      };
    }
  };

  // Sahyogi states are driven directly inline using frames

  return (
    <div
      className="wf-visualizer-root"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 540,
        background: "rgba(255, 255, 255, 0.45)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: 28,
        border: "1.5px solid rgba(5,60,97,0.08)",
        padding: "20px",
        boxShadow: "0 28px 70px rgba(5,60,97,0.06), inset 0 0 0 1.5px rgba(255,255,255,0.75)",
        overflow: "hidden",
        fontFamily: "var(--font-sans, system-ui, sans-serif)",
      }}
    >
      {/* ── Keyframes and Visual Animations ── */}
      <style>{`
        @keyframes flow-dashes {
          to { stroke-dashoffset: -20; }
        }
        @keyframes pulse-sonar {
          0% { transform: scale(0.85); opacity: 0.8; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes pulse-halo {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.15; }
          50% { transform: translate(-50%, -50%) scale(1.18); opacity: 0.35; }
        }
        @keyframes spin-hud {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes voice-wave-1 {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1.2); }
        }
        @keyframes voice-wave-2 {
          0%, 100% { transform: scaleY(0.7); }
          50% { transform: scaleY(1.5); }
        }
        @keyframes lipi-spinner {
          100% { transform: rotate(360deg); }
        }
        .anim-flow { animation: flow-dashes 1.2s linear infinite; }
        .anim-flow-reverse { animation: flow-dashes 1.6s linear infinite reverse; }
        .anim-spin-hud { animation: spin-hud 15s linear infinite; }
        .anim-spin-hud-rev { animation: spin-hud 10s linear infinite reverse; }
        .anim-pulse-halo { animation: pulse-halo 4s ease-in-out infinite; }
        
        .voice-bar {
          width: 3px;
          border-radius: 99px;
          transition: background 0.3s ease;
        }
        .lipi-mini-loader {
          width: 8px;
          height: 8px;
          border: 1.5px solid rgba(5,60,97,0.15);
          border-top-color: #2A9E5B;
          border-radius: 50%;
          animation: lipi-spinner 0.6s linear infinite;
        }
        .claim-mini-loader {
          width: 8px;
          height: 8px;
          border: 1.5px solid rgba(5,60,97,0.15);
          border-top-color: #B88500;
          border-radius: 50%;
          animation: lipi-spinner 0.6s linear infinite;
        }
        .sahyogi-mini-loader {
          width: 8px;
          height: 8px;
          border: 1.5px solid rgba(5,60,97,0.15);
          border-top-color: #CC2B58;
          border-radius: 50%;
          animation: lipi-spinner 0.6s linear infinite;
        }

        /* ─── Responsive Visualizer Centering & Scaling Overrides ─── */
        @media (max-width: 991px) {
          .page-hero__visual--aiwf {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            position: relative !important;
            width: 100% !important;
            max-width: 100% !important;
            height: 434px !important;
            margin-top: 16px !important;
            margin-bottom: 24px !important;
            overflow: hidden !important;
            padding: 0 !important;
          }
          .wf-visualizer-root {
            position: absolute !important;
            top: 50% !important;
            left: 50% !important;
            width: 540px !important;
            max-width: none !important;
            transform: translate(-50%, -50%) scale(0.94) !important;
            transform-origin: center center !important;
            margin: 0 !important;
          }
        }

        @media (max-width: 680px) {
          .page-hero__visual--aiwf {
            height: 406px !important;
          }
          .wf-visualizer-root {
            transform: translate(-50%, -50%) scale(0.88) !important;
          }
        }

        @media (max-width: 580px) {
          .page-hero__visual--aiwf {
            height: 378px !important;
          }
          .wf-visualizer-root {
            transform: translate(-50%, -50%) scale(0.82) !important;
          }
        }

        @media (max-width: 480px) {
          .page-hero__visual--aiwf {
            height: 346px !important;
            margin-top: -12px !important;
            margin-bottom: 12px !important;
          }
          .wf-visualizer-root {
            transform: translate(-50%, -50%) scale(0.75) !important;
          }
        }

        @media (max-width: 410px) {
          .page-hero__visual--aiwf {
            height: 305px !important;
          }
          .wf-visualizer-root {
            transform: translate(-50%, -50%) scale(0.66) !important;
          }
        }

        @media (max-width: 360px) {
          .page-hero__visual--aiwf {
            height: 268px !important;
          }
          .wf-visualizer-root {
            transform: translate(-50%, -50%) scale(0.58) !important;
          }
        }

        @media (max-width: 320px) {
          .page-hero__visual--aiwf {
            height: 231px !important;
          }
          .wf-visualizer-root {
            transform: translate(-50%, -50%) scale(0.50) !important;
          }
        }
      `}</style>

      {/* Dynamic inline variables to control theme */}
      <div style={{ display: "none", ["--wf-color" as any]: wf.color }} />

      {/* ── Visual Theme Backglow (Hardware Accelerated Cross-fade) ── */}
      {WORKFLOWS.map((item) => {
        const isSelected = item.id === active;
        return (
          <div
            key={`bgglow-${item.id}`}
            style={{
              position: "absolute",
              top: "40%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 250,
              height: 250,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${item.glowColor} 0%, transparent 70%)`,
              zIndex: 0,
              pointerEvents: "none",
              opacity: isSelected ? 1 : 0,
              transition: "opacity 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
            }}
          />
        );
      })}

      {/* ── Top Tabs Bar ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 6,
          background: "rgba(5,60,97,0.04)",
          padding: 4,
          borderRadius: 16,
          marginBottom: 24,
          position: "relative",
          zIndex: 2,
        }}
      >
        {WORKFLOWS.map((item) => {
          const isSelected = item.id === active;
          return (
            <button
              key={item.id}
              onClick={() => {
                isHoveredRef.current = false;
                setActive(item.id);
              }}
              style={{
                position: "relative",
                background: "transparent",
                border: "none",
                borderRadius: 12,
                padding: "10px 4px 8px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 5,
                transition: "all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)",
                outline: "none",
                zIndex: 1,
              }}
            >
              {isSelected && (
                <motion.div
                  layoutId="active-tab-pill"
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "#fff",
                    borderRadius: 12,
                    boxShadow: "0 4px 14px rgba(5,60,97,0.06), 0 1px 2px rgba(5,60,97,0.04)",
                    zIndex: -1,
                  }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <span
                style={{
                  color: isSelected ? item.color : "rgba(5,60,97,0.40)",
                  transition: "color 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  transform: isSelected ? "scale(1.1)" : "scale(1)",
                }}
              >
                <Icon name={item.icon} width={17} height={17} />
              </span>
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: isSelected ? 800 : 600,
                  color: isSelected ? "#053C61" : "rgba(5,60,97,0.45)",
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                {item.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Interactive Workspace Simulator Grid ── */}
      <div
        onMouseEnter={() => {
          isHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          isHoveredRef.current = false;
        }}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 60px 1.15fr",
          alignItems: "center",
          height: 340,
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Background SVG connections & animated flow lines */}
        <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }}>
          <svg width="100%" height="100%">
            {/* Input to Core Paths */}
            <path d="M 175 100 Q 230 100 230 170" fill="none" stroke={wf.color} strokeOpacity={0.15} strokeWidth={1.5} />
            <path
              d="M 175 170 L 230 170"
              fill="none"
              stroke={wf.color}
              strokeWidth={2}
              strokeOpacity={0.35}
              strokeDasharray="5 5"
              className="anim-flow"
            />
            <path d="M 175 240 Q 230 240 230 170" fill="none" stroke={wf.color} strokeOpacity={0.15} strokeWidth={1.5} />

            {/* Core to Output Paths */}
            <path
              d="M 285 170 Q 285 100 340 100"
              fill="none"
              stroke={wf.color}
              strokeWidth={1.5}
              strokeOpacity={0.25}
              strokeDasharray="4 6"
              className="anim-flow-reverse"
            />
            <path
              d="M 285 170 L 340 170"
              fill="none"
              stroke={wf.color}
              strokeWidth={2}
              strokeOpacity={0.5}
              strokeDasharray="5 5"
              className="anim-flow"
            />
            <path
              d="M 285 170 Q 285 240 340 240"
              fill="none"
              stroke={wf.color}
              strokeWidth={1.5}
              strokeOpacity={0.25}
              strokeDasharray="4 6"
              className="anim-flow"
            />
          </svg>
        </div>

        {/* ── 1. LEFT CARD: Raw Input Feed ── */}
        <div style={{
          position: "relative",
          zIndex: 2,
          height: active === "voice" ? 300 : active === "lipi" ? 340 : active === "claim" ? 280 : active === "sahyogi" ? 270 : 230,
          width: "100%",
          transition: "height 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
        }}>


          <AnimatePresence>
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
              style={{
                position: "absolute",
                inset: 0,
                background: "#fff",
                border: "1.5px solid rgba(5,60,97,0.07)",
                borderRadius: 20,
                padding: active === "lipi" ? "10px 10px" : "16px 14px",
                boxShadow: "0 12px 30px rgba(5,60,97,0.03), inset 0 2px 4px rgba(255,255,255,0.8)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {/* Header inside Left Card */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span
                  style={{
                    fontSize: 8.5,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    color: "rgba(5,60,97,0.45)",
                    textTransform: "uppercase",
                  }}
                >
                  {active === "sahyogi" ? getSahyogiStepInfo(frame).leftHeader : t.rawInput}
                </span>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: (active === "voice") ? "#E53E3E" : wf.color,
                    boxShadow: (active === "voice") ? "0 0 6px #E53E3E" : `0 0 6px ${wf.color}`,
                    animation: (active === "voice") ? "wf-pulse-glow 1s ease-in-out infinite" : "none",
                  }}
                />
              </div>

              {/* Central Area based on Workflow */}
              <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                {active === "voice" && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {/* Centered Microphone with Double Ripple Sonar Rings */}
                    <div
                      style={{
                        position: "relative",
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: "rgba(10,158,154,0.08)",
                        border: "1.5px solid rgba(10,158,154,0.22)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: wf.color,
                        margin: "12px 0 8px",
                        zIndex: 1,
                      }}
                    >
                      <Icon name="micro" width={22} height={22} />

                      {/* Ripple Ring 1 */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: "50%",
                          border: `1.5px solid ${wf.color}`,
                          animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite",
                          pointerEvents: "none",
                          zIndex: -1,
                        }}
                      />
                      {/* Ripple Ring 2 */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: "50%",
                          border: `1.5px solid ${wf.color}`,
                          animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite 0.75s",
                          pointerEvents: "none",
                          zIndex: -1,
                        }}
                      />
                    </div>

                    {/* Equalizer Frequency Bar Graph */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3.5,
                        height: 18,
                        margin: "4px 0 10px",
                        width: "80%",
                      }}
                    >
                      <div className="voice-bar" style={{ height: 6, background: wf.color, animation: "voice-wave-1 0.7s ease-in-out infinite" }} />
                      <div className="voice-bar" style={{ height: 14, background: wf.color, animation: "voice-wave-2 0.9s ease-in-out infinite 0.15s" }} />
                      <div className="voice-bar" style={{ height: 8, background: wf.color, animation: "voice-wave-1 0.5s ease-in-out infinite 0.3s" }} />
                      <div className="voice-bar" style={{ height: 16, background: wf.color, animation: "voice-wave-2 0.8s ease-in-out infinite 0.1s" }} />
                      <div className="voice-bar" style={{ height: 10, background: wf.color, animation: "voice-wave-1 0.6s ease-in-out infinite 0.4s" }} />
                      <div className="voice-bar" style={{ height: 5, background: wf.color, animation: "voice-wave-2 0.7s ease-in-out infinite 0.2s" }} />
                    </div>

                    {/* Real-time speech transcription typing bubble */}
                    <div
                      style={{
                        background: "rgba(5,60,97,0.03)",
                        border: "1px solid rgba(5,60,97,0.05)",
                        borderRadius: 10,
                        padding: "6px 8px",
                        width: "100%",
                        minHeight: 52,
                        boxSizing: "border-box",
                      }}
                    >
                      <span style={{ fontSize: 7, fontWeight: 800, color: "rgba(5,60,97,0.40)", display: "block", textTransform: "uppercase", marginBottom: 3 }}>
                        Doctor Speaking
                      </span>
                      <p style={{ margin: 0, fontSize: 9.5, lineHeight: 1.35, color: "#053C61", fontWeight: 500 }}>
                        {voiceInput}
                        <span style={{ display: "inline-block", width: 1.5, height: 9, background: wf.color, marginLeft: 2, verticalAlign: "middle" }} />
                      </p>
                    </div>
                  </div>
                )}

                {active === "lipi" && (
                  <div style={{ position: "relative", margin: "4px 0", display: "flex", justifyContent: "center", height: 240 }}>
                    {/* Prescription sheet mockup */}
                    <div
                      style={{
                        border: "1.5px solid rgba(5,60,97,0.08)",
                        borderRadius: 14,
                        background: "#ffffff",
                        backgroundImage: "url(/Prescription_Raw.jpg)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        position: "relative",
                        overflow: "hidden",
                        aspectRatio: "1 / 1.414",
                        width: "auto",
                        height: "100%",
                        boxSizing: "border-box",
                        boxShadow: "0 4px 12px rgba(5, 60, 97, 0.05)",
                      }}
                    >
                      {/* Glowing scanning overlay */}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: 0,
                          height: lipiPhase === "scanning" ? `${lipiLaserPercent}%` : "100%",
                          background: `linear-gradient(to bottom, ${wf.color}02 0%, ${wf.color}15 85%, ${wf.color}35 100%)`,
                          opacity: lipiPhase === "scanning"
                            ? 1
                            : lipiPhase === "processing"
                              ? 0.35 + 0.12 * Math.sin(frame / 3) // pulsing breathe effect during AI mapping
                              : Math.max(0, 1 - (frame - 150) / 25), // fade out during completion
                          pointerEvents: "none",
                          zIndex: 2,
                          transition: lipiPhase === "scanning" ? "height 0.1s linear" : "opacity 0.2s ease-out",
                        }}
                      />
                      {/* Laser scanning beam synced with frame */}
                      {lipiPhase === "scanning" && (
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: getLipiLaserTop(),
                            height: 2.5,
                            background: `linear-gradient(90deg, transparent, ${wf.color}, transparent)`,
                            boxShadow: `0 0 10px ${wf.color}`,
                            zIndex: 3,
                            transition: "top 0.1s linear",
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {active === "claim" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 7, margin: "8px 0" }}>
                    {/* Pre-flight Document Header */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, padding: "0 2px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9.5, fontWeight: 700, color: "#053C61" }}>
                        <span>Patient: Ramesh Kumar</span>
                        <span style={{ color: "rgba(5,60,97,0.50)" }}>TPA #182</span>
                      </div>
                      <div style={{ fontSize: 8.5, color: "rgba(5,60,97,0.50)" }}>
                        Treatment: ICU Management · Star Health
                      </div>
                    </div>

                    {/* Pre-flight audit check warnings (turn green based on frame) */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {/* Warning 1: Identity & Beneficiary Check */}
                      <div
                        style={{
                          background: frame >= 50 ? "rgba(42,158,91,0.05)" : "rgba(229,62,62,0.05)",
                          border: `1.2px solid ${frame >= 50 ? "#2A9E5B33" : "#E53E3E33"}`,
                          borderRadius: 10,
                          padding: "8px 10px",
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          transition: "all 0.4s ease",
                        }}
                      >
                        <span style={{ color: frame >= 50 ? "#2A9E5B" : "#E53E3E", fontSize: 10.5, fontWeight: 800, transition: "color 0.4s ease" }}>
                          {frame >= 50 ? "✓" : "✖"}
                        </span>
                        <span style={{ fontSize: 9.5, color: frame >= 50 ? "#0A5480" : "#E53E3E", fontWeight: 700, transition: "color 0.4s ease" }}>
                          {frame >= 50 ? "Identity Verified" : "Identity Fraud: Patient name mismatch across documents"}
                        </span>
                        {frame >= 50 && (
                          <span style={{ fontSize: 8, color: "#2A9E5B", fontWeight: 800, background: "rgba(42,158,91,0.12)", padding: "1.5px 5px", borderRadius: 4, marginLeft: "auto" }}>
                            Matched
                          </span>
                        )}
                      </div>

                      {/* Warning 2: Main Package Procedure Validation */}
                      <div
                        style={{
                          background: frame >= 95 ? "rgba(42,158,91,0.05)" : "rgba(184,133,0,0.05)",
                          border: `1.2px solid ${frame >= 95 ? "#2A9E5B33" : "#B8850033"}`,
                          borderRadius: 10,
                          padding: "8px 10px",
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          transition: "all 0.4s ease",
                        }}
                      >
                        <span style={{ color: frame >= 95 ? "#2A9E5B" : "#B88500", fontSize: 10.5, fontWeight: 800, transition: "color 0.4s ease" }}>
                          {frame >= 95 ? "✓" : "⚠"}
                        </span>
                        <span style={{ fontSize: 9.5, color: frame >= 95 ? "#0A5480" : "#B88500", fontWeight: 700, transition: "color 0.4s ease" }}>
                          {frame >= 95 ? t.procedureValidated : t.procedureError}
                        </span>
                        {frame >= 95 && (
                          <span style={{ fontSize: 8, color: "#2A9E5B", fontWeight: 800, background: "rgba(42,158,91,0.12)", padding: "1.5px 5px", borderRadius: 4, marginLeft: "auto" }}>
                            {t.notes}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {active === "sahyogi" && (
                  <div
                    ref={sahyogiChatRef}
                    style={{ display: "flex", flexDirection: "column", gap: 6, margin: "4px 0", height: 185, overflowY: "hidden", fontSize: 9, fontFamily: "sans-serif" }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, flexGrow: 1 }}>
                      {/* Message 1 & 2: visible in all phases under 140 */}
                      {frame < 140 ? (
                        <>
                          {/* Bot: What symptoms today? */}
                          <div style={{ alignSelf: "flex-start", background: "rgba(5, 60, 97, 0.05)", padding: "5px 8px", borderRadius: "10px 10px 10px 2px", maxWidth: "85%", color: "#053C61", fontWeight: 500 }}>
                            {locale === "hi" ? "नमस्ते! आज आपको क्या लक्षण महसूस हो रहे हैं?" : "Hi! What symptoms are you experiencing today?"}
                          </div>
                          
                          {/* Patient responded */}
                          {frame >= 20 && (
                            <div style={{ alignSelf: "flex-end", background: "rgba(204, 43, 88, 0.08)", border: "1px solid rgba(204,43,88,0.12)", padding: "5px 8px", borderRadius: "10px 10px 2px 10px", maxWidth: "85%", color: "#CC2B58", fontWeight: 600 }}>
                              {frame < 55 
                                ? (locale === "hi" ? "नमस्ते, मुझे बुखार और सूखी खांसी है।" : "Hi, I have a fever and dry cough.").slice(0, Math.floor((frame - 20) * 1.2)) 
                                : (locale === "hi" ? "नमस्ते, मुझे बुखार और सूखी खांसी है।" : "Hi, I have a fever and dry cough.")}
                              {frame >= 20 && frame < 55 && <span style={{ display: "inline-block", width: 1, height: 8, background: "#CC2B58", marginLeft: 2 }} />}
                            </div>
                          )}
                        </>
                      ) : null}

                      {/* Message 3 & 4: Phase 2 & Phase 3 */}
                      {frame >= 70 && frame < 210 && (
                        <>
                          {/* Bot: Any breathing difficulty? */}
                          <div style={{ alignSelf: "flex-start", background: "rgba(5, 60, 97, 0.05)", padding: "5px 8px", borderRadius: "10px 10px 10px 2px", maxWidth: "85%", color: "#053C61", fontWeight: 500 }}>
                            {locale === "hi" ? "समझ गया। क्या सांस लेने में कोई तकलीफ या सीने में दर्द है?" : "Understood. Any breathing difficulty or chest pain?"}
                          </div>

                          {/* Patient responded */}
                          {frame >= 95 && (
                            <div style={{ alignSelf: "flex-end", background: "rgba(204, 43, 88, 0.08)", border: "1px solid rgba(204,43,88,0.12)", padding: "5px 8px", borderRadius: "10px 10px 2px 10px", maxWidth: "85%", color: "#CC2B58", fontWeight: 600 }}>
                              {frame < 130 
                                ? (locale === "hi" ? "नहीं, लेकिन मेरा बुखार 101 F के आसपास है।" : "No, but my fever is around 101 F.").slice(0, Math.floor((frame - 95) * 1.2)) 
                                : (locale === "hi" ? "नहीं, लेकिन मेरा बुखार 101 F के आसपास है।" : "No, but my fever is around 101 F.")}
                              {frame >= 95 && frame < 130 && <span style={{ display: "inline-block", width: 1, height: 8, background: "#CC2B58", marginLeft: 2 }} />}
                            </div>
                          )}
                        </>
                      )}

                      {/* Message 5 & 6: Phase 3 */}
                      {frame >= 140 && frame < 210 && (
                        <>
                          {/* Bot: Recommend GP */}
                          <div style={{ alignSelf: "flex-start", background: "rgba(5, 60, 97, 0.05)", padding: "5px 8px", borderRadius: "10px 10px 10px 2px", maxWidth: "85%", color: "#053C61", fontWeight: 500 }}>
                            {locale === "hi" ? "मैं जनरल फिजिशियन से परामर्श करने की सलाह देता हूँ। क्या मैं इसे शेड्यूल करूँ?" : "I recommend consulting a General Physician. Shall I schedule it?"}
                          </div>

                          {/* Patient responded */}
                          {frame >= 165 && (
                            <div style={{ alignSelf: "flex-end", background: "rgba(204, 43, 88, 0.08)", border: "1px solid rgba(204,43,88,0.12)", padding: "5px 8px", borderRadius: "10px 10px 2px 10px", maxWidth: "85%", color: "#CC2B58", fontWeight: 600 }}>
                              {frame < 195 
                                ? (locale === "hi" ? "हाँ, कृपया आज के लिए ही शेड्यूल करें।" : "Yes, please schedule for today.").slice(0, Math.floor((frame - 165) * 1.2)) 
                                : (locale === "hi" ? "हाँ, कृपया आज के लिए ही शेड्यूल करें।" : "Yes, please schedule for today.")}
                              {frame >= 165 && frame < 195 && <span style={{ display: "inline-block", width: 1, height: 8, background: "#CC2B58", marginLeft: 2 }} />}
                            </div>
                          )}
                        </>
                      )}

                      {/* Message 7, 8 & 9: Phase 4 scroll view */}
                      {frame >= 210 && (
                        <>
                          {/* Bot: Slots available */}
                          <div style={{ alignSelf: "flex-start", background: "rgba(5, 60, 97, 0.05)", padding: "5px 8px", borderRadius: "10px 10px 10px 2px", maxWidth: "85%", color: "#053C61", fontWeight: 500 }}>
                            {locale === "hi" ? (
                              <>डॉ. अनन्या शर्मा आज उपलब्ध हैं: <strong style={{ color: "#CC2B58" }}>4:30 PM</strong> या <strong style={{ color: "#CC2B58" }}>5:15 PM</strong>। कौन सा ठीक रहेगा?</>
                            ) : (
                              <>Dr. Ananya Sharma available today: <strong style={{ color: "#CC2B58" }}>4:30 PM</strong> or <strong style={{ color: "#CC2B58" }}>5:15 PM</strong>. Which works?</>
                            )}
                          </div>

                          {/* Patient confirms */}
                          {frame >= 230 && (
                            <div style={{ alignSelf: "flex-end", background: "rgba(204, 43, 88, 0.08)", border: "1px solid rgba(204,43,88,0.12)", padding: "5px 8px", borderRadius: "10px 10px 2px 10px", maxWidth: "85%", color: "#CC2B58", fontWeight: 600 }}>
                              {frame < 248 
                                ? (locale === "hi" ? "4:30 PM ठीक रहेगा।" : "4:30 PM works.").slice(0, Math.floor((frame - 230) * 1.2)) 
                                : (locale === "hi" ? "4:30 PM ठीक रहेगा।" : "4:30 PM works.")}
                              {frame >= 230 && frame < 248 && <span style={{ display: "inline-block", width: 1, height: 8, background: "#CC2B58", marginLeft: 2 }} />}
                            </div>
                          )}

                          {/* Bot confirmation */}
                          {frame >= 250 && (
                            <div style={{ alignSelf: "flex-start", background: "rgba(42, 158, 91, 0.06)", border: "1px solid rgba(42, 158, 91, 0.12)", padding: "5px 8px", borderRadius: "10px 10px 10px 2px", maxWidth: "85%", color: "#2A9E5B", fontWeight: 700 }}>
                              {locale === "hi" ? "बुकिंग की पुष्टि: बुकिंग ID: CHIK-8921. विवरण लाइव चैट पर भेज दिया गया है।" : "Confirm Booking: Booking ID: CHIK-8921. Sent details to live chat."}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer info inside Left Card */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 9, color: "rgba(5,60,97,0.50)" }}>
                {active === "voice" && (
                  <>
                    <span style={{ color: "#E53E3E", fontWeight: 700 }}>{t.recLive}</span>
                    <span>99.4% {t.acc}</span>
                  </>
                )}
                {active === "lipi" && (
                  <>
                    {lipiPhase === "scanning" && (
                      <>
                        <span style={{ color: wf.color, fontWeight: 700 }}>{t.ocrScanning}</span>
                        <span>{t.confidence}: 98.7%</span>
                      </>
                    )}
                    {lipiPhase === "processing" && (
                      <>
                        <span style={{ color: "#7B1FA2", fontWeight: 700 }}>{t.aiProcessing}</span>
                        <span>{t.formularyMap}</span>
                      </>
                    )}
                    {lipiPhase === "complete" && (
                      <>
                        <span style={{ color: wf.color, fontWeight: 700 }}>{t.scanComplete}</span>
                        <span>{t.confidence}: 99.8%</span>
                      </>
                    )}
                  </>
                )}
                {active === "claim" && (
                  <>
                    <span>{t.ruleset}: NHCX v1.2</span>
                    <span style={{ color: frame >= 95 ? "#2A9E5B" : "#B88500", fontWeight: 700 }}>
                      {frame >= 95 ? (locale === "hi" ? "✓ सत्यापित" : "✓ Validated") : t.auditing}
                    </span>
                  </>
                )}
                {active === "sahyogi" && (
                  <>
                    <span>{t.triageChatbot}</span>
                    <span style={{ color: frame >= 210 ? "#2A9E5B" : frame >= 140 ? "#B88500" : "#CC2B58", fontWeight: 800 }}>
                      {frame >= 210 ? (locale === "hi" ? "● पुष्टि हुई" : "● Confirmed") : frame >= 140 ? (locale === "hi" ? "● मेल खाया" : "● Matched") : (locale === "hi" ? "● सक्रिय" : "● Active")}
                    </span>
                  </>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── 2. CENTER: Glowing Core Engine ── */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative", zIndex: 3 }}>
          {/* Radial Backglow */}
          <div
            style={{
              position: "absolute",
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: wf.color,
              filter: "blur(24px)",
              opacity: 0.32,
              transition: "background 0.5s ease",
            }}
          />

          {/* Double ring structure */}
          <div
            className="anim-pulse-halo"
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: 90,
              height: 90,
              borderRadius: "50%",
              border: `1.5px solid ${wf.color}45`,
              pointerEvents: "none",
            }}
          />

          {/* Outer rotating HUD ring */}
          <div
            className="anim-spin-hud"
            style={{
              position: "absolute",
              width: 66,
              height: 66,
              borderRadius: "50%",
              border: `1.5px solid ${wf.color}35`,
              pointerEvents: "none",
            }}
          />
          {/* Inner counter-rotating HUD ring */}
          <div
            className="anim-spin-hud-rev"
            style={{
              position: "absolute",
              width: 58,
              height: 58,
              borderRadius: "50%",
              border: `1px solid ${wf.color}18`,
              pointerEvents: "none",
            }}
          />

          {/* Solid Central Orb (Cross-faded Gradients) */}
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              position: "relative",
              zIndex: 4,
              boxShadow: `0 8px 24px ${wf.glowColor}, inset 0 0 12px rgba(255,255,255,0.45)`,
              transition: "box-shadow 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
              overflow: "hidden",
            }}
          >
            {WORKFLOWS.map((item) => {
              const isSelected = item.id === active;
              return (
                <div
                  key={`orb-${item.id}`}
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(135deg, ${item.gradientFrom}, ${item.gradientTo})`,
                    opacity: isSelected ? 1 : 0,
                    transition: "opacity 0.6s cubic-bezier(0.25, 1, 0.5, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name="sparkle" width={20} height={20} style={{ color: "#fff" }} />
                </div>
              );
            })}
          </div>
        </div>

        {/* ── 3. RIGHT CARD: Premium Structured Outputs ── */}
        <div style={{
          position: "relative",
          zIndex: 2,
          height: active === "voice" ? 300 : active === "lipi" ? 340 : active === "claim" ? 280 : active === "sahyogi" ? 270 : 230,
          width: "100%",
          transition: "height 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
        }}>
          <AnimatePresence>
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
              style={{
                position: "absolute",
                inset: 0,
                background: "#fff",
                border: "1.5px solid rgba(5,60,97,0.07)",
                borderRadius: 20,
                padding: active === "lipi" ? "10px 10px" : "16px 14px",
                boxShadow: "0 12px 30px rgba(5,60,97,0.03), inset 0 2px 4px rgba(255,255,255,0.8)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {/* Header inside Right Card */}
              <div style={{ display: "flex", alignItems: "center", justifyItems: "center", gap: 5 }}>
                <span
                  style={{
                    fontSize: 8.5,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    color: wf.color,
                    textTransform: "uppercase",
                  }}
                >
                  {active === "sahyogi" ? getSahyogiStepInfo(frame).rightHeader : t.structuredOutput}
                </span>
                <span
                  style={{
                    fontSize: 8.5,
                    fontWeight: 700,
                    color: wf.color,
                    background: `${wf.color}15`,
                    padding: "1px 5px",
                    borderRadius: 4,
                  }}
                >
                  {active === "voice"
                    ? t.generating
                    : active === "lipi"
                    ? t.digitalReport
                    : active === "claim"
                    ? t.claimIqAuditing
                    : getSahyogiStepInfo(frame).badge}
                </span>
              </div>

              {/* Main Body outputs based on active workflow */}
              <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                {active === "voice" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 3.5, margin: "4px 0" }}>
                    {/* Chief Complaint */}
                    {voiceComplaint && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        style={{ fontSize: 8.5, padding: "3.5px 7px", background: "rgba(10,158,154,0.04)", border: "1px solid rgba(10,158,154,0.08)", borderRadius: 7 }}
                      >
                        <span style={{ fontWeight: 800, color: wf.color }}>{locale === "hi" ? "शिकायत: " : "COMPLAINT: "}</span>
                        {voiceComplaint}
                        {voiceComplaint.length < VOICE_COMPLAINT_FULL.length && (
                          <span style={{ display: "inline-block", width: 1.5, height: 9, background: wf.color, marginLeft: 2 }} />
                        )}
                      </motion.div>
                    )}

                    {/* Vitals */}
                    {voiceVitals && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        style={{ fontSize: 8.5, padding: "3.5px 7px", background: "rgba(10,158,154,0.04)", border: "1px solid rgba(10,158,154,0.08)", borderRadius: 7 }}
                      >
                        <span style={{ fontWeight: 800, color: wf.color }}>{locale === "hi" ? "सजीव संकेत: " : "VITALS: "}</span>
                        {voiceVitals}
                        {voiceVitals.length < VOICE_VITALS_FULL.length && (
                          <span style={{ display: "inline-block", width: 1.5, height: 9, background: wf.color, marginLeft: 2 }} />
                        )}
                      </motion.div>
                    )}

                    {/* Diagnosis Summary */}
                    {voiceDiagnosis && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        style={{ fontSize: 8.5, padding: "3.5px 7px", background: "rgba(10,158,154,0.04)", border: "1px solid rgba(10,158,154,0.08)", borderRadius: 7 }}
                      >
                        <span style={{ fontWeight: 800, color: wf.color }}>{locale === "hi" ? "निदान: " : "DIAGNOSIS: "}</span>
                        {voiceDiagnosis}
                        {voiceDiagnosis.length < VOICE_DIAGNOSIS_FULL.length && (
                          <span style={{ display: "inline-block", width: 1.5, height: 9, background: wf.color, marginLeft: 2 }} />
                        )}
                      </motion.div>
                    )}

                    {/* Medicine */}
                    {voiceMedicine && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        style={{ fontSize: 8.5, padding: "3.5px 7px", background: "rgba(10,158,154,0.04)", border: "1px solid rgba(10,158,154,0.08)", borderRadius: 7 }}
                      >
                        <span style={{ fontWeight: 800, color: wf.color }}>{locale === "hi" ? "दवा: " : "MEDICINE: "}</span>
                        {voiceMedicine}
                        {voiceMedicine.length < VOICE_MEDICINE_FULL.length && (
                          <span style={{ display: "inline-block", width: 1.5, height: 9, background: wf.color, marginLeft: 2 }} />
                        )}
                      </motion.div>
                    )}

                    {/* Investigation */}
                    {voiceInvestigation && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        style={{ fontSize: 8.5, padding: "3.5px 7px", background: "rgba(10,158,154,0.04)", border: "1px solid rgba(10,158,154,0.08)", borderRadius: 7 }}
                      >
                        <span style={{ fontWeight: 800, color: wf.color }}>{locale === "hi" ? "जांच: " : "INVESTIGATION: "}</span>
                        {voiceInvestigation}
                        {voiceInvestigation.length < VOICE_INVESTIGATION_FULL.length && (
                          <span style={{ display: "inline-block", width: 1.5, height: 9, background: wf.color, marginLeft: 2 }} />
                        )}
                      </motion.div>
                    )}

                    {/* Habits */}
                    {voiceHabits && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        style={{ fontSize: 8.5, padding: "3.5px 7px", background: "rgba(10,158,154,0.04)", border: "1px solid rgba(10,158,154,0.08)", borderRadius: 7 }}
                      >
                        <span style={{ fontWeight: 800, color: wf.color }}>{locale === "hi" ? "आदतें: " : "HABITS: "}</span>
                        {voiceHabits}
                        {voiceHabits.length < VOICE_HABITS_FULL.length && (
                          <span style={{ display: "inline-block", width: 1.5, height: 9, background: wf.color, marginLeft: 2 }} />
                        )}
                      </motion.div>
                    )}

                    {!voiceComplaint && (
                      <div style={{ fontSize: 9.5, color: "rgba(5,60,97,0.30)", textAlign: "center", padding: "10px 0", fontStyle: "italic" }}>
                        Waiting for dictation...
                      </div>
                    )}
                  </div>
                )}

                {active === "lipi" && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: 240, margin: "4px 0" }}>
                    <AnimatePresence mode="wait">
                      {lipiPhase === "scanning" && (
                        <motion.div
                          key="scanning"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.25 }}
                          style={{
                            border: "1.5px solid rgba(5,60,97,0.08)",
                            borderRadius: 14,
                            background: "#ffffff",
                            aspectRatio: "1 / 1.414",
                            height: "100%",
                            width: "auto",
                            boxSizing: "border-box",
                            boxShadow: "0 4px 12px rgba(5, 60, 97, 0.05)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "0 14px",
                          }}
                        >
                          {/* Pulsing Scanner Core */}
                          <div style={{ position: "relative", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", margin: "10px 0 6px" }}>
                            {/* Inner core */}
                            <div
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                background: `${wf.color}12`,
                                border: `1.5px solid ${wf.color}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                                position: "relative",
                              }}
                            >
                              <Icon name="sparkle" width={18} height={18} style={{ color: wf.color }} />
                            </div>

                            {/* Ripple Sonar Rings */}
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "50%",
                                border: `1.5px solid ${wf.color}`,
                                animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "50%",
                                border: `1.5px solid ${wf.color}`,
                                animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite 0.65s",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "50%",
                                border: `1.5px solid ${wf.color}`,
                                animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite 1.3s",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />
                          </div>

                          <h4 style={{ fontSize: 10, fontWeight: 800, color: "#053C61", margin: "10px 0 0 0", textAlign: "center", letterSpacing: "0.02em", textTransform: "uppercase" }}>
                            Scanning & OCR
                          </h4>
                          <p style={{ fontSize: 8, fontWeight: 500, color: "rgba(5, 60, 97, 0.5)", margin: "4px 0 0 0", textAlign: "center", lineHeight: 1.3 }}>
                            Advanced OCR Engine parsing scribbled Rx...
                          </p>
                          <div style={{ marginTop: 12, display: "flex", gap: 3 }}>
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: wf.color, animation: "voice-wave-1 0.6s infinite" }} />
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: wf.color, animation: "voice-wave-2 0.6s infinite 0.15s" }} />
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: wf.color, animation: "voice-wave-1 0.6s infinite 0.3s" }} />
                          </div>
                        </motion.div>
                      )}

                      {lipiPhase === "processing" && (
                        <motion.div
                          key="processing"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.25 }}
                          style={{
                            border: "1.5px solid rgba(5,60,97,0.08)",
                            borderRadius: 14,
                            background: "#ffffff",
                            aspectRatio: "1 / 1.414",
                            height: "100%",
                            width: "auto",
                            boxSizing: "border-box",
                            boxShadow: "0 4px 12px rgba(5, 60, 97, 0.05)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "0 14px",
                          }}
                        >
                          {/* Pulsing AI Processor Core */}
                          <div style={{ position: "relative", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", margin: "10px 0 6px" }}>
                            {/* Inner core */}
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                background: "rgba(123, 31, 162, 0.08)",
                                border: "1.5px solid #7B1FA2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                                position: "relative",
                              }}
                            >
                              <Icon name="sparkle" width={18} height={18} style={{ color: "#7B1FA2" }} />
                            </motion.div>

                            {/* Ripple Sonar Rings */}
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "50%",
                                border: "1.5px solid #7B1FA2",
                                animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "50%",
                                border: "1.5px solid #7B1FA2",
                                animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite 0.65s",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "50%",
                                border: "1.5px solid #7B1FA2",
                                animation: "pulse-sonar 2s cubic-bezier(0.1, 0.8, 0.3, 1) infinite 1.3s",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />
                          </div>

                          <h4 style={{ fontSize: 10, fontWeight: 800, color: "#053C61", margin: "10px 0 0 0", textAlign: "center", letterSpacing: "0.02em", textTransform: "uppercase" }}>
                            AI Structuring
                          </h4>
                          <p style={{ fontSize: 8, fontWeight: 500, color: "rgba(5, 60, 97, 0.5)", margin: "4px 0 0 0", textAlign: "center", lineHeight: 1.3 }}>
                            Mapping dosage rules & formulary databases...
                          </p>
                          <div style={{ marginTop: 12, display: "flex", gap: 3 }}>
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#7B1FA2", animation: "voice-wave-1 0.6s infinite" }} />
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#7B1FA2", animation: "voice-wave-2 0.6s infinite 0.15s" }} />
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#7B1FA2", animation: "voice-wave-1 0.6s infinite 0.3s" }} />
                          </div>
                        </motion.div>
                      )}

                      {lipiPhase === "complete" && (
                        <motion.div
                          key="complete"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.4, ease: "easeOut" }}
                          style={{
                            border: "1.5px solid rgba(5,60,97,0.08)",
                            borderRadius: 14,
                            background: "#ffffff",
                            backgroundImage: "url(/Prescription_Digital.png)",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            position: "relative",
                            overflow: "hidden",
                            aspectRatio: "1 / 1.414",
                            height: "100%",
                            width: "auto",
                            boxSizing: "border-box",
                            boxShadow: "0 4px 12px rgba(5, 60, 97, 0.05)",
                          }}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {active === "claim" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, margin: "6px 0" }}>
                    {/* Live Rejection Risk Graph Badge */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 9.5, color: "rgba(5,60,97,0.50)", fontWeight: 700 }}>REJECTION RISK:</span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: parseFloat(getClaimRejectionRisk()) > 10 ? "#E53E3E" : parseFloat(getClaimRejectionRisk()) > 2 ? "#B88500" : "#2A9E5B",
                          background: parseFloat(getClaimRejectionRisk()) > 10 ? "rgba(229,62,62,0.12)" : parseFloat(getClaimRejectionRisk()) > 2 ? "rgba(184,133,0,0.12)" : "rgba(42,158,91,0.12)",
                          padding: "3px 8px",
                          borderRadius: 6,
                          transition: "all 0.2s ease",
                        }}
                      >
                        {getClaimRejectionRisk()}
                      </span>
                    </div>

                    {/* Live verification checklist output */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                      {/* Check 1 */}
                      {frame >= 25 && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5 }}
                        >
                          {frame >= 50 ? (
                            <span style={{ color: "#2A9E5B", display: "flex" }}><Icon name="check" width={11} height={11} /></span>
                          ) : (
                            <div className="claim-mini-loader" />
                          )}
                          <span style={{ color: "#053C61", fontWeight: frame >= 50 ? 700 : 500 }}>
                            {frame >= 50 ? "Beneficiary Match: Name alignment verified" : "Checking name mismatch & identity fraud..."}
                          </span>
                        </motion.div>
                      )}

                      {/* Check 2 */}
                      {frame >= 70 && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5 }}
                        >
                          {frame >= 95 ? (
                            <span style={{ color: "#2A9E5B", display: "flex" }}><Icon name="check" width={11} height={11} /></span>
                          ) : (
                            <div className="claim-mini-loader" />
                          )}
                          <span style={{ color: "#053C61", fontWeight: frame >= 95 ? 700 : 500 }}>
                            {frame >= 95 
                              ? (locale === "hi" ? "प्रक्रिया मिलान: निदान के साथ कोड सत्यापित" : "Procedure Match: Code verified with diagnosis") 
                              : (locale === "hi" ? "प्रक्रिया-निदान बेमेल को हल किया जा रहा है..." : "Resolving procedure-diagnosis mismatch...")}
                          </span>
                        </motion.div>
                      )}

                      {/* Check 3 */}
                      {frame >= 120 && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5 }}
                        >
                          {frame >= 145 ? (
                            <span style={{ color: "#2A9E5B", display: "flex" }}><Icon name="check" width={11} height={11} /></span>
                          ) : (
                            <div className="claim-mini-loader" />
                          )}
                          <span style={{ color: "#053C61", fontWeight: frame >= 145 ? 700 : 500 }}>
                            {frame >= 145 
                              ? (locale === "hi" ? "दस्तावेज़ ठीक: ऑपरेटिव नोट्स संलग्न" : "Documents OK: Operative notes attached") 
                              : (locale === "hi" ? "अनिवार्य नैदानिक दस्तावेज़ीकरण का सत्यापन..." : "Verifying mandatory clinical documentation...")}
                          </span>
                        </motion.div>
                      )}

                      {/* Final claim payload state */}
                      {frame >= 170 && (
                        <motion.div
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            background: "rgba(42,158,91,0.06)",
                            border: "1.5px solid rgba(42,158,91,0.15)",
                            borderRadius: 8,
                            padding: "6px 8px",
                            marginTop: 4,
                            textAlign: "center",
                          }}
                        >
                          <span style={{ fontSize: 9, fontWeight: 800, color: "#2A9E5B" }}>
                            {locale === "hi" ? "✓ प्री-ऑथ पैक और हस्ताक्षरित (NHCX तैयार)" : "✓ Pre-auth packaged & signed (NHCX ready)"}
                          </span>
                        </motion.div>
                      )}
                    </div>
                  </div>
                )}

                {active === "sahyogi" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, margin: "6px 0", height: 185, justifyContent: "center" }}>
                    {/* Phase 1: Symptom Triage (Frames 0 - 69) */}
                    {frame < 70 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: "#CC2B58" }}>{locale === "hi" ? "» आवक लक्षणों का विश्लेषण" : "» INGESTING INCOMING SYMPTOMS"}</div>
                        <div style={{ background: "rgba(204,43,88,0.03)", border: "1.2px solid rgba(204,43,88,0.1)", borderRadius: 8, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 3, fontFamily: "var(--font-mono, monospace)" }}>
                          <div style={{ fontSize: 8.5, color: "#053C61" }}><strong>{locale === "hi" ? "चैनल:" : "Channel:"}</strong> {locale === "hi" ? "लाइव चैट" : "Live Chat"}</div>
                          <div style={{ fontSize: 8.5, color: "#053C61" }}><strong>{locale === "hi" ? "इनपुट:" : "Input:"}</strong> {locale === "hi" ? "\"बुखार और सूखी खांसी\"" : "\"fever and dry cough\""}</div>
                          <div style={{ fontSize: 8, color: "rgba(5, 60, 97, 0.5)" }}>{locale === "hi" ? "वर्गीकरण NLP वर्गीकरण सक्रिय..." : "Triage NLP classification active..."}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                          <div className="sahyogi-mini-loader" />
                          <span style={{ fontSize: 8, color: "rgba(5, 60, 97, 0.45)" }}>{t.parsingIntent}</span>
                        </div>
                      </div>
                    )}

                    {/* Phase 2: Severity & Red Flags (Frames 70 - 139) */}
                    {frame >= 70 && frame < 140 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: "#CC2B58" }}>{locale === "hi" ? "» लक्षण गंभीरता रिपोर्ट" : "» SYMPTOM SEVERITY REPORT"}</div>
                        <div style={{ background: "rgba(204,43,88,0.03)", border: "1.2px solid rgba(204,43,88,0.1)", borderRadius: 8, padding: "6px 8px", display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "4px 8px", fontFamily: "var(--font-mono, monospace)" }}>
                          <div style={{ fontSize: 8, fontWeight: 700, color: "#CC2B58" }}>{locale === "hi" ? "बुखार:" : "Fever:"}</div>
                          <div style={{ fontSize: 8, color: "#053C61", fontWeight: 600 }}>{locale === "hi" ? "101°F (मध्यम)" : "101°F (Moderate)"}</div>
                          
                          <div style={{ fontSize: 8, fontWeight: 700, color: "#CC2B58" }}>{locale === "hi" ? "खांसी:" : "Cough:"}</div>
                          <div style={{ fontSize: 8, color: "#053C61", fontWeight: 600 }}>{locale === "hi" ? "सूखी, कफ रहित" : "Dry, Non-productive"}</div>
                          
                          <div style={{ fontSize: 8, fontWeight: 700, color: "#CC2B58" }}>{locale === "hi" ? "रेड फ्लैग:" : "Red Flags:"}</div>
                          <div style={{ fontSize: 8, color: "#2A9E5B", fontWeight: 700 }}>{locale === "hi" ? "कोई नहीं (सांस लेने में कोई तकलीफ नहीं)" : "None (No dyspnea)"}</div>
                          
                          <div style={{ fontSize: 8, fontWeight: 700, color: "#CC2B58" }}>{locale === "hi" ? "गंभीरता:" : "Severity:"}</div>
                          <div style={{ fontSize: 8, color: "#B88500", fontWeight: 700 }}>{locale === "hi" ? "हल्का / श्रेणी हरा" : "Mild / Category Green"}</div>
                        </div>
                        <div style={{ fontSize: 8, color: "rgba(5,60,97,0.45)", fontStyle: "italic" }}>
                          {locale === "hi" ? "सुझाया गया: तीव्र संदिग्ध फ्लू" : "Suggested: Acute Suspected Flu"}
                        </div>
                      </div>
                    )}

                    {/* Phase 3: Specialist Matching (Frames 140 - 209) */}
                    {frame >= 140 && frame < 210 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: "#CC2B58" }}>{locale === "hi" ? "» EMR डॉक्टर मिलान इंजन" : "» EMR DOCTOR MATCHING ENGINE"}</div>
                        <div style={{ background: "rgba(204, 43, 88, 0.03)", border: "1.2px solid rgba(204,43,88,0.1)", borderRadius: 8, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 3, fontFamily: "var(--font-mono, monospace)" }}>
                          <div style={{ fontSize: 8.5, color: "#053C61" }}><strong>{locale === "hi" ? "विशेषज्ञता:" : "Specialty:"}</strong> {locale === "hi" ? "सामान्य चिकित्सा" : "General Medicine"}</div>
                          <div style={{ fontSize: 8.5, color: "#CC2B58", fontWeight: 700 }}><strong>{locale === "hi" ? "सर्वश्रेष्ठ मिलान:" : "Best Match:"}</strong> {locale === "hi" ? "डॉ. अनन्या शर्मा" : "Dr. Ananya Sharma"}</div>
                          <div style={{ fontSize: 8, color: "rgba(5,60,97,0.6)" }}>{locale === "hi" ? "तीव्रता श्रेणी हरा उपलब्धता रोस्टर से मेल खाता है।" : "Matches severity Category Green availability roster."}</div>
                        </div>
                        <div style={{ fontSize: 8, color: "#2A9E5B", fontWeight: 700 }}>
                          {locale === "hi" ? "✓ डॉक्टर रोस्टर सफलतापूर्वक प्राप्त किया गया।" : "✓ Doctor roster fetched successfully."}
                        </div>
                      </div>
                    )}

                    {/* Phase 4: Slot Booking Sync (Frames 210 - 280) */}
                    {frame >= 210 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: "#2A9E5B" }}>{locale === "hi" ? "» HIMS अपॉइंटमेंट सिंक" : "» HIMS APPOINTMENT SYNC"}</div>
                        <div style={{ background: "rgba(42,158,91,0.04)", border: "1.2px solid rgba(42,158,91,0.15)", borderRadius: 8, padding: "6px 8px", fontFamily: "var(--font-mono, monospace)", fontSize: 8, color: "#1A6033", display: "flex", flexDirection: "column", gap: 1.5 }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>"doctor_id":</span>
                            <span style={{ fontWeight: 700 }}>"DOC-ANANYA-04"</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>"slot_time":</span>
                            <span style={{ fontWeight: 700 }}>{locale === "hi" ? "\"16:30 (आज)\"" : "\"16:30 (Today)\""}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>"booking_id":</span>
                            <span style={{ fontWeight: 700 }}>"CHIK-8921"</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>"emr_sync":</span>
                            <span style={{ fontWeight: 700, color: "#2A9E5B" }}>"COMPLETED"</span>
                          </div>
                        </div>
                        <div style={{ fontSize: 8, color: "#2A9E5B", fontWeight: 700 }}>
                          {locale === "hi" ? "✓ अपॉइंटमेंट HIMS लेजर में सिंक हो गया।" : "✓ Appointment synced in HIMS ledger."}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer info inside Right Card */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 9, color: "rgba(5,60,97,0.50)" }}>
                {active === "voice" && (
                  <>
                    <span>Format: SOAP</span>
                    <span>{locale === "hi" ? "SNOMED कोडित" : "SNOMED Coded"}</span>
                  </>
                )}
                {active === "lipi" && (
                  <>
                    {lipiPhase === "scanning" && (
                      <>
                        <span>{locale === "hi" ? "OCR इंजन: पार्सिंग जारी" : "OCR Engine: Parsing"}</span>
                        <span style={{ color: wf.color, fontWeight: 700 }}>{locale === "hi" ? "प्रगति:" : "Progress:"} {Math.round((frame / 90) * 100)}%</span>
                      </>
                    )}
                    {lipiPhase === "processing" && (
                      <>
                        <span>{locale === "hi" ? "AI मैपर: संरचना जारी" : "AI Mapper: Structuring"}</span>
                        <span style={{ color: "#7B1FA2", fontWeight: 700 }}>{locale === "hi" ? "प्रोसेसिंग..." : "Processing..."}</span>
                      </>
                    )}
                    {lipiPhase === "complete" && (
                      <>
                        <span>{locale === "hi" ? "AI प्रतिक्रिया: संरचित" : "AI Response: Structured"}</span>
                        <span style={{ color: wf.color, fontWeight: 700 }}>{locale === "hi" ? "✓ मैप और रूट किया गया" : "✓ Mapped & Routed"}</span>
                      </>
                    )}
                  </>
                )}
                {active === "claim" && (
                  <>
                    <span>{locale === "hi" ? "सत्यापन दर: 100%" : "Verify Rate: 100%"}</span>
                    <span>{locale === "hi" ? "अस्वीकृति जोखिम: 0.8%" : "Rejection Risk: 0.8%"}</span>
                  </>
                )}
                {active === "sahyogi" && (
                  <>
                    <span>{locale === "hi" ? "लेज़र सिंक: सक्रिय" : "Ledger Sync: Active"}</span>
                    <span style={{ color: frame >= 210 ? "#2A9E5B" : frame >= 140 ? "#B88500" : "#CC2B58", fontWeight: 700 }}>
                      {frame >= 210 ? (locale === "hi" ? "✓ सिंक सफल" : "✓ Sync Success") : frame >= 140 ? (locale === "hi" ? "● EMR मिलान जारी..." : "● Matching EMR...") : (locale === "hi" ? "● वर्गीकरण विश्लेषण जारी..." : "● Parsing Triage...")}
                    </span>
                  </>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ── Visualizer Footer Metric Stats ── */}
      <div
        style={{
          borderTop: "1.5px solid rgba(5,60,97,0.05)",
          paddingTop: 14,
          marginTop: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        <span style={{ fontSize: 10, color: "rgba(5,60,97,0.45)", fontWeight: 600 }}>
          {locale === "hi" ? "चिकित्सा AI इंजन" : "Chikitsa AI Engine"}
        </span>
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 800,
            color: wf.color,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            display: "flex",
            alignItems: "center",
            gap: 4.5,
            transition: "color 0.3s ease",
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: wf.color,
              display: "inline-block",
              boxShadow: `0 0 4px ${wf.color}`,
              transition: "background 0.3s ease",
            }}
          />
          {wf.shortDesc}
        </span>
      </div>
    </div>
  );
}
